import type { ToolCall } from "@models/chat";
import BuildIcon from "@mui/icons-material/BuildRounded";
import CheckIcon from "@mui/icons-material/CheckRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopyRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { extractSvg } from "@utils/svgExtract";
import { useState } from "react";
import { ArtifactView } from "./ArtifactView";
import { SchematicSvg } from "./SchematicSvg";

interface ToolCallsPanelProps {
  calls: ToolCall[];
}

/**
 * "Show your work" panel rendered under an assistant message. Lists every
 * tool the agent called while producing the reply, with each invocation's
 * arguments, return value, and any artifacts (plots, schematics) it
 * emitted.
 *
 * Collapsed by default — long traces (30+ calls on a design review) would
 * otherwise dominate the chat. The outer accordion also collapses the
 * whole panel so a user who never opens it sees only a one-line summary.
 */
export function ToolCallsPanel({ calls }: ToolCallsPanelProps) {
  const [open, setOpen] = useState(false);
  if (!calls || calls.length === 0) return null;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Accordion
        expanded={open}
        onChange={(_e, v) => setOpen(v)}
        disableGutters
        elevation={0}
        sx={{
          bgcolor: "surface.sunken",
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
          "&:before": { display: "none" },
          "&.Mui-expanded": { my: 0 },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 36,
            px: 1.5,
            "& .MuiAccordionSummary-content": { my: 0.5, gap: 1 },
          }}
        >
          <BuildIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              fontSize: "0.7rem",
            }}
          >
            Show work · {calls.length}{" "}
            {calls.length === 1 ? "tool call" : "tool calls"}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1, py: 0.5 }}>
          <Stack spacing={0.75}>
            {calls.map((c, i) => (
              <ToolCallRow key={`${c.name}-${i}`} call={c} />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

function ToolCallRow({ call }: { call: ToolCall }) {
  const [open, setOpen] = useState(false);
  const argsCount = Object.keys(call.args ?? {}).length;
  const summary =
    call.summary && call.summary.length > 0 ? call.summary : "completed";

  return (
    <Accordion
      expanded={open}
      onChange={(_e, v) => setOpen(v)}
      disableGutters
      elevation={0}
      sx={{
        bgcolor: "surface.raised",
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: 1.5,
        "&:before": { display: "none" },
        "&.Mui-expanded": { my: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
        sx={{
          minHeight: 32,
          px: 1.25,
          "& .MuiAccordionSummary-content": {
            my: 0.5,
            gap: 1,
            alignItems: "center",
            minWidth: 0,
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: (t) => t.fonts.mono,
            fontSize: "0.78rem",
            fontWeight: 600,
            color: "secondary.main",
            flexShrink: 0,
          }}
        >
          {call.name || "tool"}
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: "0.78rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {summary}
        </Typography>
        {/* "via <Tool>" provenance chip was retired with the workbench
            tool picker (May 2026). All tools are baseline now, so no
            owning Tool to attribute the call to. */}
        {argsCount > 0 && (
          <Chip
            size="small"
            label={`${argsCount} ${argsCount === 1 ? "arg" : "args"}`}
            sx={{
              height: 18,
              fontSize: "0.65rem",
              bgcolor: "surface.sunken",
              color: "text.secondary",
            }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.25, pb: 1.25, pt: 0 }}>
        {argsCount > 0 && <Section label="Arguments" value={call.args} />}
        {call.result !== null && call.result !== undefined && (
          <Section label="Result" value={call.result} />
        )}
        {call.artifacts && call.artifacts.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <SectionLabel>Artifacts</SectionLabel>
            <Stack spacing={0.75}>
              {call.artifacts.map((a, i) => (
                <ArtifactView key={`${a.url}-${i}`} artifact={a} />
              ))}
            </Stack>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "text.secondary",
        fontWeight: 600,
        fontSize: "0.65rem",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function Section({ label, value }: { label: string; value: unknown }) {
  // Strings: if they contain an SVG block, render it inline (schematics).
  // Otherwise show as a code block. Structured values get JSON-stringified.
  if (typeof value === "string") {
    const { text, svgs } = extractSvg(value);
    return (
      <Box sx={{ mt: 1 }}>
        <SectionLabel>{label}</SectionLabel>
        {text && <CodeView text={text} />}
        {svgs.map((svg, i) => (
          <SchematicSvg key={`svg-${i}`} svg={svg} />
        ))}
      </Box>
    );
  }
  let pretty: string;
  try {
    pretty = JSON.stringify(value, null, 2);
  } catch {
    pretty = String(value);
  }
  return (
    <Box sx={{ mt: 1 }}>
      <SectionLabel>{label}</SectionLabel>
      <CodeView text={pretty} />
    </Box>
  );
}

function CodeView({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };
  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "surface.sunken",
        border: "1px solid",
        borderColor: "border.strong",
        borderLeft: "2px solid",
        borderLeftColor: "border.accent",
        borderRadius: 1,
        "&:hover .toolcall-copy-btn": { opacity: 1 },
      }}
    >
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.25,
          fontFamily: (t) => t.fonts.mono,
          fontSize: "0.75rem",
          lineHeight: 1.5,
          color: "text.primary",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          maxHeight: 320,
          overflowY: "auto",
        }}
      >
        {text}
      </Box>
      <Tooltip title={copied ? "Copied" : "Copy"} placement="left">
        <IconButton
          className="toolcall-copy-btn"
          size="small"
          onClick={handleCopy}
          aria-label="Copy"
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            opacity: 0,
            transition: (t) => `opacity ${t.motion.fast}`,
            p: "3px",
            bgcolor: "surface.raised",
            "&:hover": { bgcolor: "surface.raisedHover" },
          }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 12, color: "success.main" }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 12 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
